import { useState, useEffect, useCallback } from 'react';
import { joinPersonName } from '@/utils/names';
import {
  Plus, X, Loader2, CreditCard, Search, ChevronDown, ChevronUp, Trash2,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Invoice {
  id: string;
  number: string;
  total: number;
  amountDue: number;
  contact?: { firstName: string; lastName: string } | null;
  account?: { name: string } | null;
}

interface Payment {
  id: string;
  date: string;
  invoiceId: string;
  invoiceNumber?: string;
  invoice?: { number: string; contact?: { firstName: string; lastName: string } | null; account?: { name: string } | null };
  amount: number;
  method: string;
  reference?: string;
  tdsRate?: number;
  tdsAmount?: number;
  netAmount?: number;
  notes?: string;
  contactName?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const METHODS = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Credit Card', 'Other'];

// ─── Main Component ───────────────────────────────────────────────────────────

export function PaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [page, setPage] = useState(1);
  const limit = 20;

  const [form, setForm] = useState({
    invoiceId: '',
    amount: '',
    method: 'Bank Transfer',
    reference: '',
    tdsRate: '0',
    tdsAmount: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [invRes] = await Promise.all([
        api.get('/invoices'),
      ]);
      const invoiceList: Invoice[] = invRes.data.data?.invoices ?? invRes.data.data ?? [];
      setInvoices(invoiceList);

      // Collect payments from all invoices with payments data
      const allPayments: Payment[] = [];
      for (const inv of invoiceList) {
        try {
          const payRes = await api.get(`/invoices/${inv.id}/payments`);
          const invPayments = payRes.data.data?.payments ?? payRes.data.data ?? [];
          for (const p of invPayments) {
            allPayments.push({
              ...p,
              invoiceNumber: inv.number,
              contactName: inv.contact ? `${joinPersonName(inv.contact.firstName, inv.contact.lastName)}` : inv.account?.name || '-',
            });
          }
        } catch {
          // Skip invoices with no payments endpoint
        }
      }
      setPayments(allPayments);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Auto-calculate TDS
  const selectedInvoice = invoices.find((i) => i.id === form.invoiceId);
  const tdsRate = parseFloat(form.tdsRate) || 0;
  const paymentAmount = parseFloat(form.amount) || 0;
  const computedTds = (paymentAmount * tdsRate) / 100;
  const computedNet = paymentAmount - computedTds;

  const handleCreate = async () => {
    if (!form.invoiceId || !form.amount) return;
    try {
      setSaving(true);
      await api.post(`/invoices/${form.invoiceId}/payments`, {
        amount: paymentAmount,
        method: form.method,
        reference: form.reference || undefined,
        tdsRate: tdsRate || undefined,
        tdsAmount: computedTds || undefined,
        notes: form.notes || undefined,
      });
      setShowModal(false);
      setForm({ invoiceId: '', amount: '', method: 'Bank Transfer', reference: '', tdsRate: '0', tdsAmount: '', notes: '' });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  // Summary calculations — parse to number to avoid string concatenation
  const n = (v: any) => (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0;
  const totalReceived = payments.reduce((s, p) => s + n(p.netAmount ?? p.amount), 0);
  const totalPending = invoices.reduce((s, inv) => s + n(inv.amountDue), 0);

  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonthPayments = payments
    .filter((p) => new Date(p.date) >= thisMonthStart)
    .reduce((s, p) => s + n(p.netAmount ?? p.amount), 0);

  // Filter & sort
  const filtered = payments.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.invoiceNumber || '').toLowerCase().includes(q) ||
      (p.contactName || '').toLowerCase().includes(q) ||
      (p.method || '').toLowerCase().includes(q)
    );
  });

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const sorted = [...filtered].sort((a, b) => {
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (sortField === 'date') { aVal = new Date(aVal).getTime(); bVal = new Date(bVal).getTime(); }
    if (sortField === 'amount') { aVal = Number(aVal); bVal = Number(bVal); }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalFiltered = sorted.length;
  const totalPages = Math.ceil(totalFiltered / limit);
  const paginated = sorted.slice((page - 1) * limit, page * limit);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
          <p className="text-sm text-gray-500">Track received payments and collections</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Record Payment
        </button>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Total Received</p>
          <p className="mt-1 text-lg font-bold text-green-600">{formatINR(totalReceived)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Pending</p>
          <p className="mt-1 text-lg font-bold text-orange-600">{formatINR(totalPending)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">This Month</p>
          <p className="mt-1 text-lg font-bold text-[var(--ink-0)]">{formatINR(thisMonthPayments)}</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search payments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading payments...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchData} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <CreditCard size={40} className="mb-2" />
            <p className="text-sm">No payments found</p>
            <p className="text-xs">Record your first payment to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('date')}>
                    <span className="flex items-center gap-1">Date <SortIcon field="date" /></span>
                  </th>
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort('amount')}>
                    <span className="flex items-center justify-end gap-1">Amount <SortIcon field="amount" /></span>
                  </th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3 text-right">TDS</th>
                  <th className="px-4 py-3 text-right">Net Amount</th>
                  <th className="px-4 py-3 text-center w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-600">{formatDate(p.date)}</td>
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{p.invoiceNumber || p.invoice?.number || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{p.contactName || (p.invoice?.contact ? `${joinPersonName(p.invoice.contact.firstName, p.invoice.contact.lastName)}` : '-')}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(p.amount)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{p.method}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{p.tdsAmount ? formatINR(p.tdsAmount) : '-'}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatINR(p.netAmount ?? p.amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this payment?')) {
                            api.delete(`/invoices/${p.invoiceId}/payments/${p.id}`).then(() => fetchData()).catch(() => alert('Failed to delete'));
                          }
                        }}
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, totalFiltered)} of {totalFiltered}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Record Payment Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)} className="!bg-white">
            <div className="sticky top-0 z-10 bg-white rounded-t-xl flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Record Payment</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Invoice *</label>
                <select
                  value={form.invoiceId}
                  onChange={(e) => setForm({ ...form, invoiceId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select invoice</option>
                  {invoices.filter((i) => i.amountDue > 0).map((inv) => {
                    const contactName = inv.contact ? `${joinPersonName(inv.contact.firstName, inv.contact.lastName)}`.trim() : null;
                    const accountName = inv.account?.name;
                    const who = contactName || accountName;
                    return (
                      <option key={inv.id} value={inv.id}>
                        {who ? `${who} — ` : ''}{inv.number} — Due: {formatINR(inv.amountDue)}
                      </option>
                    );
                  })}
                </select>
                {selectedInvoice && (
                  <p className="mt-1 text-xs text-gray-500">Amount Due: {formatINR(selectedInvoice.amountDue)}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Amount (INR) *</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Method *</label>
                  <select
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    {METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reference / Transaction ID</label>
                <input
                  type="text"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="e.g. UTR number"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">TDS Rate (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    step="0.1"
                    value={form.tdsRate}
                    onChange={(e) => setForm({ ...form, tdsRate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">TDS Amount (auto)</label>
                  <input
                    type="text"
                    readOnly
                    value={computedTds > 0 ? formatINR(computedTds) : '-'}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                  />
                </div>
              </div>

              {paymentAmount > 0 && (
                <div className="rounded-lg bg-green-50 p-3 text-sm">
                  <span className="text-gray-600">Net Amount: </span>
                  <span className="font-bold text-green-700">{formatINR(computedNet)}</span>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button
                onClick={handleCreate}
                disabled={saving || !form.invoiceId || !form.amount}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Record Payment
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
